import { Controller } from "@hotwired/stimulus"
import { post, get, destroy } from "@rails/request.js"

export default class extends Controller {
  static targets = ["taxSelect"]
  static values = { teamId: String }

  connect() {
    this.hot = null
    this.currentPage = 1
    this.totalPages = 1
    this.isLoading = false
    this.rules = []

    const urlParams = new URLSearchParams(window.location.search)
    const taxId = urlParams.get('tax_id')
    if (taxId) {
      this.taxSelectTarget.value = taxId
      this.onTaxChange()
    }
  }

  async onTaxChange() {
    const option = this.taxSelectTarget.options[this.taxSelectTarget.selectedIndex]
    const container = document.getElementById('config-link-container')
    const link = document.getElementById('config-columns-link')

    if (!option.value) {
      this.clearGrid()
      container.classList.add('hidden')
      return
    }

    container.classList.remove('hidden')
    link.href = `/account/teams/${this.teamIdValue}/rules/configure_columns?tax_id=${option.value}`

    await this.initGrid()
  }

  async initGrid() {
    const taxId = this.taxSelectTarget.value
    if (!taxId) return

    this.currentPage = 1
    this.rules = []
    
    const columns = this.getGridColumns()
    const container = document.getElementById('rules-handsontable')
    
    if (this.hot) {
      this.hot.destroy()
    }

    // eslint-disable-next-line no-undef
    this.hot = new Handsontable(container, {
      data: this.rules,
      columns: columns,
      rowHeaders: true,
      colHeaders: columns.map(c => c.title),
      height: 600,
      width: '100%',
      stretchH: 'all',
      licenseKey: 'non-commercial-and-evaluation',
      minSpareRows: 1,
      afterChange: (changes, source) => {
        if (source === 'loadData') return
        this.handleChanges(changes)
      },
      beforeRemoveRow: (index, amount) => {
        this.handleRemoveRow(index, amount)
      }
    })

    // Setup infinite scroll
    this.setupInfiniteScroll(container)

    // Load initial data
    await this.loadRules(taxId)
    this.hot.loadData(this.rules)
  }

  setupInfiniteScroll(container) {
    // Wait for Handsontable to render and create wtHolder
    const interval = setInterval(() => {
      const wtHolder = container.querySelector('.wtHolder')
      if (wtHolder) {
        clearInterval(interval)
        wtHolder.addEventListener('scroll', () => {
          if (wtHolder.scrollTop + wtHolder.clientHeight >= wtHolder.scrollHeight - 50) {
            if (this.currentPage < this.totalPages && !this.isLoading) {
              this.loadMoreRules()
            }
          }
        })
      }
    }, 100)
  }

  getGridColumns() {
    const option = this.taxSelectTarget.options[this.taxSelectTarget.selectedIndex]
    const conditionCols = JSON.parse(option.dataset.conditionColumns || "[]")
    const consequenceCols = JSON.parse(option.dataset.consequenceColumns || "[]")

    const cols = [
      { data: 'rule_key', title: 'Chave', readOnly: true, width: 100 },
    ]

    conditionCols.forEach(col => {
      cols.push({ data: `cond_${col}`, title: `(C) ${col}` })
    })

    consequenceCols.forEach(col => {
      cols.push({ data: `cons_${col}`, title: `(R) ${col}` })
    })

    return cols
  }

  async loadRules(taxId) {
    this.isLoading = true
    const response = await get(`/account/teams/${this.teamIdValue}/rules/tax_rules?tax_id=${taxId}&page=${this.currentPage}`)
    if (response.ok) {
      const json = await response.json
      this.rules = json.data
      this.totalPages = json.total_pages
      this.currentPage = json.current_page
    }
    this.isLoading = false
  }

  async loadMoreRules() {
    const taxId = this.taxSelectTarget.value
    this.currentPage++
    this.isLoading = true
    const response = await get(`/account/teams/${this.teamIdValue}/rules/tax_rules?tax_id=${taxId}&page=${this.currentPage}`)
    if (response.ok) {
      const json = await response.json
      
      const wtHolder = this.hot.rootElement.querySelector('.wtHolder')
      const scrollTop = wtHolder ? wtHolder.scrollTop : 0

      // Remove spare rows from the current rules array before appending new data.
      // Handsontable's minSpareRows appends empty objects to the data source array.
      while (this.rules.length > 0 && this.isRowEmpty(this.rules[this.rules.length - 1])) {
        this.rules.pop()
      }

      this.rules.push(...json.data)
      this.hot.loadData(this.rules)

      if (wtHolder) {
        setTimeout(() => {
          wtHolder.scrollTop = scrollTop
        }, 0)
      }
    }
    this.isLoading = false
  }

  isRowEmpty(row) {
    if (!row) return true
    if (row.id) return false
    // A row is considered empty if it has no values in its columns (excluding id and rule_key)
    const keys = Object.keys(row).filter(k => k.startsWith('cond_') || k.startsWith('cons_'))
    if (keys.length === 0) return true
    return keys.every(key => !row[key])
  }

  async handleChanges(changes) {
    if (!changes) return
    const taxId = this.taxSelectTarget.value
    
    const rowsToUpdate = [...new Set(changes.map(c => c[0]))]

    for (const rowIdx of rowsToUpdate) {
      const rowData = this.hot.getSourceDataAtRow(rowIdx)
      if (!rowData) continue

      // Se for linha nova e não tiver nem condição nem consequência, ignora
      if (this.isRowEmpty(rowData)) continue

      const response = await post(`/account/teams/${this.teamIdValue}/rules/batch_update`, {
        body: JSON.stringify({
          tax_id: taxId,
          rule: rowData
        })
      })

      if (response.ok) {
        const json = await response.json
        if (json.status === 'success') {
          // Atualiza o ID no objeto de dados fonte para futuras atualizações
          rowData.id = json.rule.id
          // Atualiza a chave no grid
          this.hot.setDataAtRowProp(rowIdx, 'rule_key', json.rule.rule_key, 'loadData')
        }
      } else {
        const json = await response.json
        alert("Erro ao salvar: " + (json.errors || []).join(", "))
      }
    }
  }

  async handleRemoveRow(index, amount) {
    for (let i = 0; i < amount; i++) {
      const rowData = this.hot.getSourceDataAtRow(index + i)
      if (rowData && rowData.id) {
        const response = await destroy(`/account/rules/${rowData.id}`)
        if (!response.ok) {
          alert("Erro ao excluir regra.")
        }
      }
    }
  }

  clearGrid() {
    if (this.hot) {
      this.hot.destroy()
      this.hot = null
    }
  }
}
